import { useInfractionsSignIn } from "@features/infractions/hooks/use-infractions-sign-in";
import { AlignLeft01, AlignRight01, Menu02, XClose } from "@untitledui/icons";
import { useMemo } from "react";
import {
	Button as AriaButton,
	Dialog as AriaDialog,
	DialogTrigger as AriaDialogTrigger,
	Modal as AriaModal,
	ModalOverlay as AriaModalOverlay,
} from "react-aria-components";
import { useLocation } from "react-router-dom";
import { NavList } from "@/components/application/app-navigation/base-components/nav-list";
import { AvatarLabelGroup } from "@/components/base/avatar/avatar-label-group";
import { Dropdown } from "@/components/base/dropdown/dropdown";
// import { ThemeToggle } from "@/components/theme/ThemeToggle";
import { Image } from "@/components/ui/image";
import { useAuth } from "@/context/AuthContext";
import { usePermission } from "@/hooks/use-permissions";
import { useSidebar } from "@/providers/sidebar";
import { filterNavItemsByRole } from "./filter-nav-items";
import { mapInfractionsNavItems } from "./map-infractions-nav-items";
import { sidebarStyles } from "./sidebar.styles";
import { navItems } from "./sidebar-nav-items";

export const SidebarContent = () => {
	const location = useLocation();
	const pathname = location.pathname;
	const { user: userAuth, loading, logout } = useAuth();
	const { collapsed, toggle } = useSidebar();
	const styles = useMemo(() => sidebarStyles({ collapsed }), [collapsed]);
	const { can } = usePermission();
	const { openHabilitarMeInNewTab, isSigningInHabilitarMe } =
		useInfractionsSignIn();

	const filteredNavItems = useMemo(
		() =>
			userAuth?.role
				? filterNavItemsByRole(
						navItems,
						Array.isArray(userAuth.role) ? userAuth.role : [userAuth.role],
						can
					)
				: [],
		[userAuth?.role, can]
	);

	const navItemsWithInfractionsActions = useMemo(
		() =>
			mapInfractionsNavItems(filteredNavItems, {
				openHabilitarMeInNewTab,
				isSigningInHabilitarMe,
			}),
		[filteredNavItems, openHabilitarMeInNewTab, isSigningInHabilitarMe]
	);

	if (loading) {
		return <div />;
	}

	return (
		<aside
			className={styles.desktop.aside}
			data-testid="sidebar-aside"
			style={styles.desktopStyle.aside}
		>
			<div style={styles.desktopStyle.contentWrapper}>
				<div style={styles.desktopStyle.headerRow}>
					<Image
						alt="Fleeter"
						className={styles.desktop.logo}
						height={32}
						src="/Logo.svg"
						width={120}
					/>
					<button
						aria-label="Toggle sidebar"
						className={`${styles.desktop.toggleButton} hidden lg:flex`}
						onClick={toggle}
						style={styles.desktopStyle.toggleButton}
						type="button"
					>
						{collapsed ? (
							<AlignRight01 className="size-5" />
						) : (
							<AlignLeft01 className="size-5" />
						)}
					</button>
				</div>
			</div>

			<NavList
				activeUrl={pathname}
				className={styles.desktop.navList}
				items={navItemsWithInfractionsActions}
			/>

			<div className={styles.desktop.footer}>
				<Dropdown.Root>
					<AriaButton
						className={styles.desktop.profileCard}
						style={styles.desktopStyle.profileCard}
					>
						<AvatarLabelGroup
							size="md"
							src="/icon.png"
							subtitle={userAuth?.email || ""}
							title={userAuth?.fullName || "Usuário"}
						/>
					</AriaButton>
					<Dropdown.Popover>
						<div className="flex gap-3 border-secondary border-b p-3">
							<AvatarLabelGroup
								size="md"
								src="/icon.png"
								subtitle={userAuth?.email || ""}
								title={userAuth?.fullName || "Usuário"}
							/>
						</div>
						{/* <div className="border-secondary border-b p-2">
							<ThemeToggle />
						</div> */}
						<Dropdown.Menu>
							<Dropdown.Section>
								<Dropdown.Item onClick={logout}> Sair </Dropdown.Item>
							</Dropdown.Section>
						</Dropdown.Menu>
					</Dropdown.Popover>
				</Dropdown.Root>
			</div>
		</aside>
	);
};

const Sidebar = () => {
	const { collapsed } = useSidebar();
	const styles = sidebarStyles({ collapsed });

	return (
		<>
			<AriaDialogTrigger>
				<header
					className={styles.mobile.header}
					data-testid="mobile-header"
					style={styles.mobileStyle.header}
				>
					<Image alt="Fleeter" height={32} src="/Logo.svg" width={120} />
					<AriaButton
						aria-label="Expand navigation menu"
						className={styles.mobile.openButton}
						style={styles.mobileStyle.openButton}
					>
						<Menu02 className="size-5" />
					</AriaButton>
				</header>

				<AriaModalOverlay
					className={styles.mobile.overlay}
					isDismissable
					style={styles.mobileStyle.overlay}
				>
					{({ state }) => (
						<AriaModal className={styles.mobile.modal}>
							<AriaDialog className={styles.mobile.dialog}>
								<AriaButton
									aria-label="Fechar menu de navegação"
									className={styles.mobile.closeButton}
									onPress={() => state.close()}
									style={styles.mobileStyle.closeButton}
								>
									<XClose className="size-5" />
								</AriaButton>
								<SidebarContent />
							</AriaDialog>
						</AriaModal>
					)}
				</AriaModalOverlay>
			</AriaDialogTrigger>

			{/* Desktop */}
			<div className="hidden lg:fixed lg:inset-y-0 lg:left-0 lg:flex">
				<SidebarContent />
			</div>
		</>
	);
};

export default Sidebar;
